import React from 'react';
import { Card, Checkbox, List, Space, Tag, Typography, Skeleton, theme } from 'antd';
import type { TableProps } from 'antd';
import type { ColumnType } from 'antd/es/table';

const { Text } = Typography;

/**
 * モバイルカードで表示するフィールドの役割
 */
export type MobileFieldRole = 'title' | 'description' | 'tag' | 'extra' | 'hidden';

/**
 * レスポンシブ対応のカラム型定義
 */
export interface ResponsiveColumnType<T> extends ColumnType<T> {
  /** モバイルカードでの表示役割 */
  mobileRole?: MobileFieldRole;
  /** モバイル表示での優先度（低いほど優先、未指定は最後） */
  mobilePriority?: number;
}

/**
 * モバイルカード設定
 */
export interface MobileCardConfig<T> {
  /** タイトルとして表示するフィールド */
  titleField?: keyof T | ((record: T) => React.ReactNode);
  /** 説明として表示するフィールド */
  descriptionFields?: (keyof T)[];
  /** タグとして表示するフィールド */
  tagFields?: (keyof T)[];
  /** 追加情報として表示するフィールド */
  extraFields?: (keyof T)[];
  /** アクションボタン */
  actions?: (record: T) => React.ReactNode;
  /** クリックハンドラ */
  onClick?: (record: T) => void;
}

interface MobileCardListProps<T extends object> {
  dataSource: T[];
  columns: ResponsiveColumnType<T>[];
  mobileCardConfig?: MobileCardConfig<T>;
  loading?: boolean;
  rowKey?: keyof T | ((record: T) => string);
  rowSelection?: TableProps<T>['rowSelection'];
  pagination?: {
    current?: number;
    pageSize?: number;
    total?: number;
    onChange?: (page: number, pageSize: number) => void;
  };
}

/**
 * モバイル用カードリストコンポーネント
 * テーブルデータをカード形式で表示
 */
export function MobileCardList<T extends object>({
  dataSource,
  columns,
  mobileCardConfig,
  loading,
  rowKey,
  rowSelection,
  pagination,
}: MobileCardListProps<T>): React.ReactElement {
  const { token } = theme.useToken();

  const getColumnKey = (column: ResponsiveColumnType<T>, fallback: string): string => {
    if (column.key != null) return String(column.key);
    if (column.dataIndex != null) return String(column.dataIndex);
    return fallback;
  };

  // カラムから役割別にフィールドを抽出
  const getFieldsByRole = (role: MobileFieldRole): ResponsiveColumnType<T>[] => {
    return columns
      .filter((col) => col.mobileRole === role)
      .sort((a, b) => (a.mobilePriority ?? 999) - (b.mobilePriority ?? 999));
  };

  const titleColumns = getFieldsByRole('title');
  const descriptionColumns = getFieldsByRole('description');
  const tagColumns = getFieldsByRole('tag');
  const extraColumns = getFieldsByRole('extra');

  // hidden以外でmobileRoleが未指定のカラムはdescriptionとして扱う
  const unassignedColumns = columns.filter(
    (col) => !col.mobileRole && col.dataIndex
  );

  // レコードからキーを取得
  const getRowKey = (record: T, index: number): string => {
    if (typeof rowKey === 'function') {
      return rowKey(record);
    }
    if (rowKey) {
      return String(record[rowKey]);
    }
    return String(index);
  };

  const getTextFromNode = (node: React.ReactNode): string => {
    if (typeof node === 'string' || typeof node === 'number') {
      return String(node);
    }
    if (Array.isArray(node)) {
      return node.map(getTextFromNode).join('');
    }
    if (React.isValidElement(node)) {
      return getTextFromNode((node.props as { children?: React.ReactNode }).children);
    }
    return '';
  };

  const getSelectedRowKeys = (): string[] =>
    rowSelection?.selectedRowKeys?.map((key) => String(key)) ?? [];

  const buildSelectedRows = (selectedKeys: string[]): T[] => {
    const rowByKey = new Map(
      dataSource.map((record, index) => [getRowKey(record, index), record])
    );
    return selectedKeys
      .map((key) => rowByKey.get(key))
      .filter((record): record is T => Boolean(record));
  };

  const handleSelectionChange = (record: T, index: number, checked: boolean) => {
    if (!rowSelection?.onChange) return;

    const key = getRowKey(record, index);
    const currentKeys = getSelectedRowKeys();
    const nextKeys = checked
      ? Array.from(new Set([...currentKeys, key]))
      : currentKeys.filter((selectedKey) => selectedKey !== key);

    rowSelection.onChange(nextKeys, buildSelectedRows(nextKeys), { type: 'single' });
  };

  // カラムのレンダリング値を取得
  const renderColumnValue = (
    column: ResponsiveColumnType<T>,
    record: T,
    index: number
  ): React.ReactNode => {
    if (column.render) {
      const dataIndex = column.dataIndex as keyof T | undefined;
      const value = dataIndex ? record[dataIndex] : undefined;
      const rendered = column.render(value, record, index);
      // RenderedCell型を処理
      if (rendered && typeof rendered === 'object' && 'children' in rendered) {
        return (rendered as { children: React.ReactNode }).children;
      }
      return rendered as React.ReactNode;
    }
    if (column.dataIndex) {
      const value = record[column.dataIndex as keyof T];
      return value != null ? String(value) : '-';
    }
    return null;
  };

  // タイトルを取得
  const getTitle = (record: T, index: number): React.ReactNode => {
    // mobileCardConfig.titleField が優先
    if (mobileCardConfig?.titleField) {
      if (typeof mobileCardConfig.titleField === 'function') {
        return mobileCardConfig.titleField(record);
      }
      return String(record[mobileCardConfig.titleField] ?? '');
    }
    // mobileRole='title' のカラムから
    if (titleColumns.length > 0) {
      return renderColumnValue(titleColumns[0], record, index);
    }
    // 最初のカラム
    if (columns.length > 0) {
      return renderColumnValue(columns[0], record, index);
    }
    return '';
  };

  // 説明フィールドを取得
  const getDescriptionItems = (record: T, index: number): React.ReactNode[] => {
    const items: React.ReactNode[] = [];

    // mobileCardConfig.descriptionFields
    if (mobileCardConfig?.descriptionFields) {
      mobileCardConfig.descriptionFields.forEach((field) => {
        const column = columns.find((col) => col.dataIndex === field);
        const label = column?.title ?? String(field);
        const value = record[field];
        items.push(
          <div key={String(field)} style={{ marginBottom: 4 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {typeof label === 'string' ? label : ''}:
            </Text>{' '}
            <Text style={{ fontSize: 13 }}>
              {column ? renderColumnValue(column, record, index) : String(value ?? '-')}
            </Text>
          </div>
        );
      });
    } else {
      // mobileRole='description' のカラム + 未割り当てカラム
      [...descriptionColumns, ...unassignedColumns].forEach((col) => {
        if (!col.dataIndex) return;
        const label = col.title;
        items.push(
          <div key={String(col.dataIndex)} style={{ marginBottom: 4 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {typeof label === 'string' ? label : ''}:
            </Text>{' '}
            <Text style={{ fontSize: 13 }}>{renderColumnValue(col, record, index)}</Text>
          </div>
        );
      });
    }

    return items;
  };

  // タグを取得
  const getTags = (record: T, index: number): React.ReactNode[] => {
    const tags: React.ReactNode[] = [];

    // mobileCardConfig.tagFields
    if (mobileCardConfig?.tagFields) {
      mobileCardConfig.tagFields.forEach((field) => {
        const column = columns.find((col) => col.dataIndex === field);
        const value = column ? renderColumnValue(column, record, index) : record[field];
        if (value != null) {
          tags.push(
            <span key={String(field)} style={{ marginRight: 4 }}>
              {React.isValidElement(value) ? value : <Tag>{String(value)}</Tag>}
            </span>
          );
        }
      });
    } else {
      // mobileRole='tag' のカラム
      tagColumns.forEach((col, colIndex) => {
        const value = renderColumnValue(col, record, index);
        if (value != null) {
          tags.push(
            <span key={getColumnKey(col, `tag-${colIndex}`)} style={{ marginRight: 4 }}>
              {React.isValidElement(value) ? value : <Tag>{String(value)}</Tag>}
            </span>
          );
        }
      });
    }

    return tags;
  };

  // 追加情報を取得
  const getExtra = (record: T, index: number): React.ReactNode[] => {
    const extras: React.ReactNode[] = [];
    //mobileRole:"extra"にすると[object Object]になるのを修正
    if (mobileCardConfig?.extraFields) {
      mobileCardConfig.extraFields.forEach((field) => {
        const column = columns.find((col) => col.dataIndex === field);
        const value = column ? renderColumnValue(column, record, index) : record[field];
        if (value != null) {
          extras.push(
            <span key={String(field)}>{React.isValidElement(value) ? value : String(value)}</span>
          );
        }
      });
    } else {
      extraColumns.forEach((col, colIndex) => {
        const value = renderColumnValue(col, record, index);
        if (value != null) {
          extras.push(
            <span key={getColumnKey(col, `extra-${colIndex}`)}>
              {React.isValidElement(value) ? value : String(value)}
            </span>
          );
        }
      });
    }

    return extras;
  };

  if (loading) {
    return (
      <List
        className="mobile-card-list"
        dataSource={[1, 2, 3]}
        renderItem={() => (
          <List.Item style={{ padding: '8px 0' }}>
            <Card style={{ width: '100%' }} styles={{ body: { padding: 12 } }}>
              <Skeleton active paragraph={{ rows: 2 }} />
            </Card>
          </List.Item>
        )}
      />
    );
  }

  return (
    <List
      className="mobile-card-list"
      dataSource={dataSource}
      pagination={
        pagination
          ? {
              ...pagination,
              size: 'small',
              showSizeChanger: false,
              showTotal: (total) => `${total}件`,
            }
          : false
      }
      renderItem={(record, index) => {
        const key = getRowKey(record, index);
        const title = getTitle(record, index);
        const descriptionItems = getDescriptionItems(record, index);
        const tags = getTags(record, index);
        const extras = getExtra(record, index);
        const checkboxProps = rowSelection?.getCheckboxProps?.(record);
        const selectionLabel = getTextFromNode(title) || `行${index + 1}`;
        const isSelected = getSelectedRowKeys().includes(key);

        return (
          <List.Item key={key} style={{ padding: '8px 0' }}>
            <Card
              style={{ width: '100%' }}
              styles={{ body: { padding: 12 } }}
              hoverable={!!mobileCardConfig?.onClick}
              onClick={() => mobileCardConfig?.onClick?.(record)}
            >
              {/* ヘッダー: タイトルを先に全幅で確保し、操作は下段へ逃がす */}
              <div
                className="mobile-card-list__header"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  gap: 8,
                  marginBottom: 8,
                }}
              >
                <div
                  className="mobile-card-list__title-row"
                  style={{ display: 'flex', alignItems: 'flex-start', gap: 8, minWidth: 0, width: '100%' }}
                >
                  {rowSelection && (
                    <Checkbox
                      aria-label={`${selectionLabel}を選択`}
                      checked={isSelected}
                      disabled={checkboxProps?.disabled}
                      onClick={(event) => event.stopPropagation()}
                      onChange={(event) => handleSelectionChange(record, index, event.target.checked)}
                    />
                  )}
                  <Text
                    strong
                    style={{
                      display: 'block',
                      fontSize: 15,
                      lineHeight: 1.5,
                      minWidth: 0,
                      flex: 1,
                      whiteSpace: 'normal',
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {title}
                  </Text>
                </div>
                {mobileCardConfig?.actions && (
                  <div
                    className="mobile-card-list__actions"
                    style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {mobileCardConfig.actions(record)}
                  </div>
                )}
              </div>

              {/* タグ */}
              {tags.length > 0 && (
                <div style={{ marginBottom: 8 }}>
                  <Space wrap size={4}>
                    {tags}
                  </Space>
                </div>
              )}

              {/* 説明フィールド */}
              {descriptionItems.length > 0 && (
                <div style={{ marginBottom: extras.length > 0 ? 8 : 0 }}>
                  {descriptionItems}
                </div>
              )}

              {/* 追加情報 */}
              {extras.length > 0 && (
                <div style={{ borderTop: `1px solid ${token.colorBorderSecondary}`, paddingTop: 8 }}>
                  <Space split={<span style={{ color: token.colorBorder }}>|</span>}>
                    {extras}
                  </Space>
                </div>
              )}
            </Card>
          </List.Item>
        );
      }}
    />
  );
}
